import { createApi } from '@reduxjs/toolkit/query/react';
import { filteredFilter } from '../../utils/api';
import {properties} from "../../properties.js";
import { authBaseQuery } from '../../app/axiosBaseQuery';
import { PaginationResult, ListRequest } from '../../types/global.d';
import * as Entity from '../types/models/Price';
import {createNormalizationMiddleware} from "../../app/normy/normyRtkQuery.ts";

export const PRICE_API_REDUCER_KEY = 'prices';
export const PRICE_PATH = 'prices';

export const priceApi = createApi({
    reducerPath: PRICE_API_REDUCER_KEY,
    baseQuery: authBaseQuery(),
    keepUnusedDataFor: 10,
    refetchOnMountOrArgChange: 10,
    refetchOnFocus: true,
    tagTypes: ['Price', 'Prices'],
    endpoints: (builder) => ({
        getPriceList: builder.query<PaginationResult<Entity.Price>, ListRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request))
                return ({
                    url: `/${ PRICE_PATH }?${new URLSearchParams(queryParams).toString()}`,
                    method: 'GET',
                })
            },
            providesTags: ['Prices'],
        }),
        getPrice: builder.query<Entity.Price, Entity.PrimaryPrice>({
            queryFn: async (id, { getState }, extraOptions, baseQuery) => {

                const cachedData = priceNormalizer.getObjectById(id.id);

                if (cachedData) {
                    return { data: cachedData };
                } else {
                    const response = await baseQuery({
                        url: `/${ PRICE_PATH }/${id.toPath()}`,
                        method: 'GET',
                    });

                    return response;
                }
            },
            providesTags: ['Price'],
        }),
        getPriceMultiple: builder.query<Entity.Price, Entity.PrimaryPrice[]>({
            query: (ids) => {
                return ({
                    url: `/${ PRICE_PATH }/ids${Entity.PrimaryPrice.toQueryParams(ids)}`,
                    method: 'GET',
                })
            },
            providesTags: ['Prices'],
        }),
        getPricePresentation: builder.query<PaginationResult<Entity.Price>, Entity.PresentationPriceRequest>({
            query: (request) => {
              const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
              return ({
                url: `/${ PRICE_PATH }/presentation?${new URLSearchParams(queryParams).toString()}`,
                method: 'POST',
                data: request.filter,
              })
            },
            providesTags: ['Prices'],
        }),
        getPriceAdvancedPresentation: builder.query<PaginationResult<Entity.Price>, Entity.FilterAdvancedPresentationRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
                return ({
                    url: `/${ PRICE_PATH }/find/advancedPresentation?${new URLSearchParams(queryParams).toString()}`,
                    method: 'POST',
                    data: request.filter,
                })
            },
            providesTags: ['Prices'],
        }),
        
        insertPrice: builder.mutation<Entity.Price, Entity.CreatePrice>({
            query: (toInsert) => {
                return ({
                    url: `/${ PRICE_PATH }`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                dispatch(
                    priceApi.util.upsertQueryData('getPrice', Entity.PrimaryPrice.fromId(mutationData.id), mutationData),
                );
            },
            invalidatesTags: ['Prices'],
        }),
        insertMultiplePrice: builder.mutation<Entity.Price[], Entity.CreatePrice[]>({
            query: (toInsert) => {
                return ({
                    url: `/${ PRICE_PATH }/batch`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                for(const item of mutationData) {
                    dispatch(
                            priceApi.util.upsertQueryData('getPrice', Entity.PrimaryPrice.fromId(item.id), item),
                    );
                }
            },
            invalidatesTags: ['Prices'],
        }),
        updatePrice: builder.mutation<Entity.Price, Entity.UpdateEntityPrice>({
            query: (toUpdate) => {
                return ({
                    url: `/${ PRICE_PATH }/${toUpdate.id.toPath()}`,
                    method: 'PUT',
                    data: toUpdate.data,
                })
            },
            invalidatesTags: ['Prices'],
        }),
        updateMultiplePrice: builder.mutation<Entity.Price[], Entity.UpdateEntityPrice[]>({
            query: (toUpdate) => {
                return ({
                    url: `/${ PRICE_PATH }/batch`,
                    method: 'PUT',
                    data: toUpdate,
                })
            },
            invalidatesTags: ['Prices'],
        }),
        deletePrice: builder.mutation<Entity.Price, Entity.Price>({
            query: (toDelete) => {
                return ({
                    url: `/${ PRICE_PATH }/${Entity.PrimaryPrice.fromEntity(toDelete)?.toPath()}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['Prices'],
        }),
        deleteMultiplePrice: builder.mutation<Entity.Price[], Entity.PrimaryPrice[]>({
            query: (ids) => {
                return ({
                    url: `/${ PRICE_PATH }/batch${Entity.PrimaryPrice.toQueryParams(ids)}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['Prices'],
        }),
    }),
})

export const [priceNormalizer, priceNormalizerMiddleware] = createNormalizationMiddleware(PRICE_API_REDUCER_KEY, priceApi, {
    devLogging: properties.debugLog,
})

export const {
    useGetPriceListQuery,
    useGetPriceMultipleQuery,
    useGetPricePresentationQuery,
    useGetPriceQuery,
    useInsertPriceMutation,
    useInsertMultiplePriceMutation,
    useUpdatePriceMutation,
    useUpdateMultiplePriceMutation,
    useDeletePriceMutation,
    useDeleteMultiplePriceMutation,
    useGetPriceAdvancedPresentationQuery,
    
} = priceApi
